import React, { useState, useRef, MutableRefObject, useEffect } from 'react';
import { Modal } from 'antd';
import styles from './index.module.css';
import IntegrationCatalog, { IntegrationCatalogProps } from './integrationCatalog';
import { editIntegration, createIntegration } from 'utils/integrations';
import { IntegrationSettingsProps } from '../new_project_settings/types';
import track from 'utils/analytics';
import { IntegrationModalContent } from './IntegrationModalContent';
import { ModalComplexHeader } from './ModalComplexHeader';

// TODO: this data also needs to come from the backend, we should send a hash
// for each integration.
export const visibleModals = {
  webhooks: 11430515,
  harvest: 11430577,
  teams: 11430616,
  teamwork: 11430627,
  slack: 11430538,
  trellonative: 11430565,
  jiranative: 11430555,
  github: 11430519,
  asananative: 11430560,
  linearnative: 11430519,
  clickupnative: 11430592,
  mondaynative: 0,
  productivenative: 0
};

const visibleModalKeys: string[] = Object.keys(visibleModals);
const modalKeys = [...visibleModalKeys, null] as (string | null)[];
export type VisibleModal = typeof visibleModalKeys[number];

export type ConnectButtonProps = {
  showConnect?: boolean;
  isConnected?: boolean;
  handleConnect?: () => void;
};

const IntegrationSettings: React.FC<IntegrationSettingsProps> = (
  props: IntegrationSettingsProps
) => {
  const { project, harvestIntegration, webhooksPath, apiKey } = props;
  const { id, organizationId, webhooks } = project || {};
  const getContainer = document.getElementById('settingsBoard') as HTMLElement;
  const containerRef: MutableRefObject<any> = useRef(null);
  const [visibleModal, setVisibleModal] = useState<VisibleModal | null>(null);
  const [justUpgradedSlack, setJustUpgradedSlack] = useState(false);
  const [isSlackUpgrading, setIsSlackUpgrading] = useState(false);

  const [legacyIntegrations, setLegacyIntegrations] = useState(
    props.legacyIntegrations
  );

  const hideModal = () => {
    setVisibleModal(null);
    setJustUpgradedSlack(false);
  };

  const nativeModalFor = (
    integrationName: string | null
  ): VisibleModal | null =>
    integrationName &&
    ['jira', 'asana', 'clickup', 'monday', 'productive'].includes(
      integrationName
    )
      ? `${integrationName}native`
      : (integrationName as VisibleModal | null);

  useEffect(() => {
    const openParam = new URLSearchParams(window.location.search).get(
      'open_integration'
    );
    const openIntegration = props.openIntegration || openParam;
    const modalFor = nativeModalFor(openIntegration);
    if (
      openIntegration &&
      ([...visibleModalKeys, null] as (string | null)[]).includes(modalFor)
    ) {
      setVisibleModal(modalFor);
    }
  }, []);

  const handleHarvestClick = () => {
    if (!harvestIntegration?.id) {
      track('Integration connected', {
        integration: 'harvest',
        project_id: id,
        type: 'native'
      });
      return createIntegration(String(organizationId), 'harvest');
    }

    editIntegration(String(organizationId), harvestIntegration.id);
  };

  const integrationConnectedState = (name: string) =>
    legacyIntegrations?.[name] || null;

  const organizationIntegrationForModal = () => {
    switch (visibleModal) {
      case 'harvest':
        return harvestIntegration;
      case 'teams':
        return integrationConnectedState('team');
      case 'teamwork':
        return integrationConnectedState('teamwork');
      case 'slack':
        return (
          integrationConnectedState('slack') ||
          integrationConnectedState('slack_legacy') ||
          integrationConnectedState('slacklegacy')
        );
      case 'trellonative':
        return integrationConnectedState('trello');
      case 'jiranative':
        return integrationConnectedState('jira');
      case 'github':
        return integrationConnectedState('github');
      case 'asananative':
        return integrationConnectedState('asana');
      case 'clickupnative':
        return integrationConnectedState('clickup');
      case 'linearnative':
        return integrationConnectedState('linear');
      case 'mondaynative':
        return integrationConnectedState('monday');
      case 'productivenative':
        return integrationConnectedState('productive');
      default:
        return null;
    }
  };

  const organizationManageUrl = () => {
    const integration = organizationIntegrationForModal();
    if (!integration?.id) return null;

    return `/settings/organizations/${props.project.organizationId}/integrations/${integration.id}/edit`;
  };

  const headerProps = () => {
    if (visibleModal === 'harvest')
      return {
        show: true,
        showConnect: true,
        isConnected: !!harvestIntegration?.id,
        handleConnect: handleHarvestClick,
        manageUrl: organizationManageUrl()
      };
    return { show: modalKeys.includes(visibleModal), manageUrl: organizationManageUrl() };
  };

  return (
    <div className={styles.integrations}>
      <div ref={containerRef} className={styles.container}>
        <div className={styles.container} />
        <IntegrationCatalog
          {...(props as IntegrationCatalogProps)}
          switchModal={setVisibleModal}
        />
        <Modal
          open={!!visibleModal}
          className={styles.modalContainer}
          footer={null}
          title={
            <ModalComplexHeader
              {...headerProps()}
              visibleModal={visibleModal}
              setVisibleModal={setVisibleModal}
            />
          }
          getContainer={getContainer}
          onCancel={hideModal}
        >
          <IntegrationModalContent
            visibleModal={visibleModal}
            integrationProps={{
              projectId: id,
              organizationId,
              webhooks,
              webhooksPath,
              apiKey,
              authProviders: props.authProviders || [],
              container: containerRef.current,
              legacyIntegrations,
              justUpgradedSlack,
              onDisconnect: () => hideModal(),
              harvestIntegration,
              handleHarvestClick
            }}
            reconnectHandlers={{
              isSlackUpgrading,
              setLegacyIntegrations,
              setJustUpgradedSlack,
              setIsSlackUpgrading
            }}
          />
        </Modal>
      </div>
      <div id={'integration-popover'} />
    </div>
  );
};

export default IntegrationSettings;
